/**
 * THE ACTIVITY MARK — SCOUT SAYING WHAT IT IS DOING, AND ONLY WHAT IT KNOWS.
 *
 * This is the one visual element in the workspace whose job is a claim about
 * the present tense, so it is drawn from a reading rather than from a timer —
 * see `../activity` for where a reading comes from and, more importantly, for
 * the states it refuses to invent. Everything here is presentation of a
 * decision already made.
 *
 * THREE THINGS CARRY THE STATE, AND THE ANIMATION IS THE LEAST IMPORTANT.
 *
 *   1. THE WORDS. A `role="status"` line that says what the record says. A
 *      screen reader gets it, and so does anybody who has turned animation off.
 *   2. THE COLOUR. Accent for a lane that is ticking, muted for a still one,
 *      danger for a failure — the same three meanings the rest of this
 *      stylesheet uses those tokens for, and no fourth.
 *   3. THE MOVEMENT. A ring that turns ONLY when the reading says `moving`.
 *
 * IT IS NOT A SPINNER AND IT MUST NEVER BECOME ONE. A spinner says "working"
 * without qualification, which is the exact claim this product is not entitled
 * to make: a nine-minute-old `last_segment_at` is not work, and a pending HTTP
 * request is not reasoning. So the ring's speed is slow enough to read as
 * "something is being observed", the states that are not happening do not move
 * at all, and the sentence underneath always says which fact produced it.
 */

import { ACTIVITY_VOCABULARY, type ActivityReading } from '../activity';

/** Which colour a state is drawn in. Deliberately three values and not eleven:
 *  the state NAME is what distinguishes them, and a palette with an entry per
 *  state would be inviting a reader to learn eleven colours instead of reading
 *  one sentence. */
function toneOf(reading: ActivityReading): 'live' | 'stopped' | 'failed' {
	if (reading.state === 'FAILED') return 'failed';
	return reading.moving ? 'live' : 'stopped';
}

export function ActivityMark({
	reading,
	/** What the mark is about, so the sentence reads as being about a thing —
	 *  "Research window · …" rather than a floating clause. */
	subject,
}: {
	reading: ActivityReading;
	subject?: string;
}) {
	return (
		<div class={`activity activity--${toneOf(reading)}${reading.moving ? ' is-moving' : ''}`}>
			<span class="activity-ring" aria-hidden="true" />
			{/* THE SENTENCE IS THE ELEMENT THAT CARRIES THE MEANING, and it is a
			    live region so a state change is announced rather than only seen.
			    `polite` rather than `assertive`: nothing here is an emergency, and
			    one that interrupted the Owner mid-sentence would be a mark that
			    made the product harder to use than not having one. */}
			<p class="activity-words" role="status">
				{subject !== undefined && <span class="activity-subject">{subject}</span>}
				<span class="activity-state">{reading.state}</span>
				<span class="activity-detail">{reading.wording}</span>
			</p>
		</div>
	);
}

/**
 * WHAT THE NAMES MEAN, AND WHICH ONE NOTHING PRODUCES.
 *
 * THE VOCABULARY TABLE HAD NO CALLER UNTIL THIS, AND A TABLE NOBODY RENDERS IS
 * A TABLE THAT ROTS — the same objection this repository makes to a service
 * method with no route. It is here, behind a disclosure rather than in the open,
 * because it is reference material: a reader wants the sentence beside the
 * state, not a glossary before every conversation.
 *
 * IT LISTS THE UNPRODUCED NAME AND SAYS WHY, which is the whole reason the
 * declaration is written down at all. A vocabulary that silently omitted
 * `EVIDENCE_REVIEW` would leave a reader to conclude the state is merely rare;
 * this says no column records which phase of a segment is in progress, and that
 * inferring one from a timestamp is the fabricated thinking step the product
 * forbids.
 *
 * A `<details>` IS A REAL DISCLOSURE AND NEEDS NO JAVASCRIPT — it is keyboard
 * operable, it is announced correctly, and it survives the bundle failing to
 * load, which no hand-rolled toggle does.
 */
export function ActivityVocabulary() {
	return (
		<details class="activity-vocab">
			<summary>What these names mean, and which one nothing produces</summary>
			<dl class="facts">
				{ACTIVITY_VOCABULARY.map((entry) => (
					<div key={entry.name}>
						<dt>{entry.name}</dt>
						<dd>
							{/* THE PREFIX IS THE MARKER, EXACTLY AS THE INBOX READS IT —
							    one convention for "nothing raises this", not two. */}
							{entry.producer.startsWith('NOTHING') && <span class="tag tag-warn">no producer</span>}{' '}
							{entry.producer}
						</dd>
					</div>
				))}
			</dl>
		</details>
	);
}
